<template>
  <div>
    <TheTemplateM class="p-the-page-m14">
      <template v-slot:heading>メール受信設定</template>

      <template v-slot:body>
        <LoadingContent v-if="isLoading" />
        <div class="p-the-page-m14__form c-form-inner">
          <Form @submit.prevent="submit">
            <div
              v-for="(wantMailData, key) in sortedWantMailList"
              :key="key"
              class="p-form-group p-form-group--near"
            >
              <template v-if="wantMailData.show">
                <FormLabel>{{ wantMailData.label }}
                  <!-- tooltip が定義されている項目のみ表示 -->
                  <div
                    v-if="wantMailData.tooltip"
                    class="p-mail-help-wrapper"
                    @mouseenter="showTooltipPc(wantMailData.key)"
                    @mouseleave="hideTooltipPc"
                  >
                    <div
                      class="p-mail-help"
                      @click.stop="toggleTooltip(wantMailData.key)"
                    >
                      ⓘ
                    </div>
                    <div
                      v-if="activeTooltipKey === wantMailData.key && !isSp"
                      class="p-mail-help__tooltip"
                      @click.stop
                    >
                      {{ wantMailData.tooltip }}
                    </div>
                  </div>
                </FormLabel>
                <div class="p-form-radio-group">
                  <Field v-slot="{ field, errors }" :name="wantMailData.key">
                    <InputRadio
                      v-bind="field"
                      v-model="wantMail[wantMailData.key]"
                      :value="'1'"
                      :error="errors[0]"
                    >希望する</InputRadio>
                    <InputRadio
                      v-bind="field"
                      v-model="wantMail[wantMailData.key]"
                      :value="'2'"
                      :error="errors[0]"
                    >希望しない</InputRadio>
                    <ValidationErrorText v-if="errors[0]">
                      {{ errors[0] }}
                    </ValidationErrorText>
                  </Field>
                </div>
              </template>
            </div>

            <!-- SP: タップ時はモーダルで文言を表示 -->
            <div
              v-if="activeTooltipKey && isSp"
              class="p-help-modal-overlay"
              @click="activeTooltipKey = ''"
            >
              <div
                class="p-help-modal"
                @click.stop
              >
                <div class="p-help-modal__body">
                  {{ activeTooltipText }}
                </div>
              </div>
            </div>

            <div
              v-if="!isGroupBudgetMailHiddenUser && roleType == '4' && isGroupBudget"
              style="margin-top: 12px;"
            >
              <div
                v-for="(wantMailDataForGroupBudget, key) in wantMailListForGroupBudget"
                :key="key"
                class="p-form-group p-form-group--near"
              >
                <template v-if="wantMailDataForGroupBudget.show">
                  <FormLabel>
                    <span data-translate-skip>{{ groupBudgetName }}</span>{{ wantMailDataForGroupBudget.label }}
                  </FormLabel>
                  <div class="p-form-radio-group">
                    <Field v-slot="{ field, errors }" :name="wantMailDataForGroupBudget.key">
                      <InputRadio
                        v-bind="field"
                        v-model="wantMail[wantMailDataForGroupBudget.key]"
                        value="1"
                        :error="errors[0]"
                      >希望する</InputRadio>
                      <InputRadio
                        v-bind="field"
                        v-model="wantMail[wantMailDataForGroupBudget.key]"
                        value="2"
                        :error="errors[0]"
                      >希望しない</InputRadio>
                      <ValidationErrorText v-if="errors[0]">
                        {{ errors[0] }}
                      </ValidationErrorText>
                    </Field>
                  </div>
                </template>
              </div>
            </div>

            <div class="p-form-button-group" v-if="!isLoading">
              <div v-if="hasUpdate" class="p-form-button-group__vertical">
                <ButtonMain type="submit">保存</ButtonMain>
              </div>
              <div class="p-form-button-group__vertical">
                <ButtonMain tag="a" href="/home/" kind="cancel">
                  {{ buttonTitle }}
                </ButtonMain>
              </div>
            </div>
          </Form>
        </div>
      </template>
    </TheTemplateM>

    <ModalMain v-if="modalName === 'mailDeliverySettingsUpdated'">
      <template v-slot:head>
        メール受信設定の変更が
        <br />完了しました。
      </template>
      <template v-slot:body>
        <ButtonMain kind="cancel" @click="closeModal">OK</ButtonMain>
      </template>
    </ModalMain>
  </div>
</template>

<script lang="ts">
import TheTemplateM from "@/general/components/templates/TheTemplateM.vue";
import InputRadio from "@/common/components/atoms/InputRadio.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";
import ModalMain from "@/common/components/organisms/ModalMain.vue";
import { Field } from "vee-validate";
import { Options, Vue } from "vue-class-component";
import * as yup from "yup";
import { Ref, Prop } from 'vue-property-decorator';
import commons from "@/common/store/modules/commons";
import globals from "@/general/store/modules/globals";

import modalState from "@/common/store/modules/modalState";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import SettingsApi from "@/general/api/settingsApi";
import { MailDeliverySetting } from "@/general/store/models";
import SITE_MAP from "@/general/mixins/const/custom/sitemap";
import CONST_DATA from "@/general/mixins/const/constData";
import auth from "@/general/store/modules/auth";
import AuthStorage from "@/general/utils/auth";
import loginInfo from "@/general/store/modules/loginInfo";
import AuthApi from "@/general/api/authApi";
import LocalStorage from "@/general/utils/storage";

@Options({
  props: {
    id: { type: Number, default: 1 },
    modal: { type: String, default: "", required: false }
  },
  components: {
    TheTemplateM,
    InputRadio,
    FormLabel,
    ValidationErrorText,
    Field,
    ModalMain
  }
})
export default class MailDeliverySettings extends Vue {
  formData: any = [];

  @Ref("form") form!: HTMLFormElement;
  @Prop({ type: Number, default: 1 }) id!: number;
  @Prop({ type: String, default: "", required: false }) modal!: string;
  @Prop({ type: Boolean, required: false, default: false }) ssoFlg!: boolean;

  result: any = [];
  page!: number;
  pageSize!: number;
  hasUpdate = true;
  closeModalState = modalState.closeModalState;
  changeModal = "";
  roleType = null;
  isGroupBudget = false;
  groupBudgetName = "";
  isLoading = true;
  activeTooltipKey = "";

  get modalName(): string {
    return this.changeModal;
  }

  set modalName(value: string) {
    this.changeModal = value;
  }

    wantMailList = [
    {
      key: "want_point_given_mail",
      label: "ポイント受取通知メール",
      value: "1",
      show: false,
      order: 1
    },
    {
      key: "want_point_balance_mail",
      label: "ポイント残高通知メール",
      value: "1",
      show: false,
      order: 2
    },
    {
      key: "want_point_expiration_mail",
      label: "ポイント有効期限通知メール",
      value: "1",
      show: false,
      order: 3
    },
    {
      key: "approval_result_notification_mail",
      label: "サンクスポイント承認/否認結果通知メール",
      value: "1",
      show: false,
      order: 6
    },
    {
      key: "want_mailmagazine",
      label: "メールマガジン",
      value: "1",
      show: false,
      order: 7
    },
    {
      key: "want_thanks_thread_mail",
      label: "サンクススレッドメッセージ通知メール",
      value: "1",
      show: false,
      order: 4,
      tooltip: "サンクスメッセージのスレッドに返信があった場合に送信されるメールです。"
    },
    {
      key: "want_thanks_remind_mail",
      label: "未読通知メール",
      value: "1",
      show: false,
      order: 5,
      tooltip: "サンクスメッセージのスレッドに、受信後3日間未読となっているメッセージがある場合に送信されるメールです。"
    }
  ];

  wantMailListForGroupBudget = [
    {
      key: "gb_want_point_given_mail",
      label: "GroupBudgetポイント受取通知メール",
      value: "1",
      show: false
    },
    {
      key: "gb_want_point_balance_mail",
      label: "GroupBudgetポイント残高通知メール",
      value: "1",
      show: false
    },
    {
      key: "gb_want_point_expiration_date",
      label: "GroupBudgetポイント有効期限通知メール",
      value: "1",
      show: false
    },
  ];

  breadcrumb = [
    {
      text: "",
      url: SITE_MAP.TOP_PAGE,
      pageType: "home"
    },
    {
      text: "メール受信設定",
      url: "",
      pageType: "lower"
    }
  ];

  buttonTitle = "キャンセル";

  get urls() {
    return {
      mypage: SITE_MAP.MY_PAGE
    };
  }

  wantMail: any = {
    want_point_given_mail: "",
    want_point_balance_mail: "",
    want_point_expiration_mail: "",
    want_thanks_thread_mail: "",
    want_thanks_remind_mail: "",
    approval_result_notification_mail: "",
    want_mailmagazine: "",
    gb_want_point_given_mail: "",
    gb_want_point_balance_mail: "",
    gb_want_point_expiration_date: ""
  };

  get validationSchema() {
    const schema: Record<string, any> = {};
    [...this.wantMailList, ...this.wantMailListForGroupBudget].forEach(
      (item) => {
        if (item.show) {
          schema[item.key] = yup
            .string()
            .required(`${item.label}を入力してください。`)
            .max(1, `${item.label}は1文字以内を入力してください。`);
        }
      }
    );
    return yup.object().shape(schema);
  }

  closeModal() {
   this.modalName = "";
  }
  async submit() {
    try {
      await this.validationSchema.validate(this.wantMail, { abortEarly: false });

      const result = this.userNotificationSettings;
      const updres = await SettingsApi.postMailDeliverySetting(
        result.point_receipt,
        result.point_balance,
        result.point_expiration_date,
        result.thanks_thread,
        result.thanks_remind,
        result.thanks_point_result,
        result.magazine,
        result.user,
        result.gb_point_receipt,
        result.gb_point_balance,
        result.gb_point_expiration_date
      );

      if (updres.status === 400) {
        alert(updres.data.error);
      } else if (updres.status === 500) {
        if (this.ssoFlg) {
          window.location.href = "/session-time-out/";
        } else {
          LocalStorage.removeAll();
          AuthApi.getLogout();
          auth.remove();
          window.location.href = "/system-error/";
        }
      } else {
        window.location.href = "/my-page/mailDeliverySettingsUpdated.html";
      }
    } catch (err: any) {
      if (err.name === "ValidationError") {
        alert("入力に誤りがあります。");
      } else {
        alert("通信エラーが発生しました。");
      }
    }
  }

  mounted() {
    AuthStorage.checkLogin();
  }

  async created() {
    this.changeModal = this.modal || "";
    this.ssoFlg = loginInfo.isSso;
    await commons.setOrgPageSpecificDataFromLocalStorage();

    const profileDataString = await AuthApi.getICSession();
    const profileData = JSON.parse(profileDataString);
    this.roleType = profileData.roleType;
    this.isGroupBudget = profileData.isGroupBudget;
    this.groupBudgetName = profileData.groupBudgetName;

    if (profileData.isGroupBudgetMode === true) {
      if (this.ssoFlg) {
        // SSOログイン時は行き止まりエラー画面へ
        window.location.href = "/session-time-out/";
      } else {
        LocalStorage.removeAll();
        AuthApi.getLogout();
        auth.remove();
        window.location.href = "/system-error/";
      }
      return;
    }

    const mailSetting: MailDeliverySetting = await SettingsApi.getMailDeliverySettingRead();

    this.wantMail.want_point_given_mail =
      mailSetting.user_point_receipt === true ? "1" : "2";
    this.wantMail.want_point_balance_mail =
      mailSetting.user_point_balance === true ? "1" : "2";
    this.wantMail.want_point_expiration_mail =
      mailSetting.user_point_expiration_date === true ? "1" : "2";
    this.wantMail.want_thanks_thread_mail =
        mailSetting.user_thanks_thread === true ? "1" : "2";
    this.wantMail.want_thanks_remind_mail =
        mailSetting.user_thanks_remind === true ? "1" : "2";
    this.wantMail.approval_result_notification_mail =
      mailSetting.user_thanks_point_result === true ? "1" : "2";
    this.wantMail.want_mailmagazine =
      mailSetting.user_mail_magazine === true ? "1" : "2";
    this.wantMail.gb_want_point_given_mail =
      mailSetting.user_gb_point_receipt === true ? "1" : "2";
    this.wantMail.gb_want_point_balance_mail =
      mailSetting.user_gb_point_balance === true ? "1" : "2";
    this.wantMail.gb_want_point_expiration_date =
      mailSetting.user_gb_point_expiration_date === true ? "1" : "2";

    this.wantMailList[0].show =
      mailSetting.org_point_receipt_notification_enabled;
    this.wantMailList[1].show =
      mailSetting.org_point_balance_notification_enabled;
    this.wantMailList[2].show =
      mailSetting.org_point_expiry_notification_enabled;
    this.wantMailList[3].show =
      mailSetting.approval_result;
    this.wantMailList[4].show = mailSetting.org_mail_magazine_enabled;
    this.wantMailList[5].show = mailSetting.thanks_thread_show;
    this.wantMailList[6].show = mailSetting.thanks_remind_show;

    this.wantMailList[3].label = this.thanksPointName + "承認/否認結果通知メール";

    //メール受信設定の項目をgroupbudgetNameをもとに再設定
    this.wantMailListForGroupBudget[0].label =
      "受取通知メール";
    this.wantMailListForGroupBudget[1].label =
      "残高通知メール";
    this.wantMailListForGroupBudget[2].label =
      "有効期限通知メール";

    this.wantMailListForGroupBudget[0].show =
      mailSetting.org_is_gb_point_receipt
    this.wantMailListForGroupBudget[1].show =
      mailSetting.org_is_gb_point_balance
    this.wantMailListForGroupBudget[2].show =
      mailSetting.org_is_gb_point_expiration_date

    this.hasUpdate =
      this.wantMailList[0].show ||
      this.wantMailList[1].show ||
      this.wantMailList[2].show ||
      this.wantMailList[3].show ||
      this.wantMailList[4].show ||
      this.wantMailList[5].show ||
      this.wantMailList[6].show ||
      this.wantMailListForGroupBudget[0].show ||
      this.wantMailListForGroupBudget[1].show ||
      this.wantMailListForGroupBudget[2].show

    this.buttonTitle = this.hasUpdate ? this.buttonTitle : "戻る";

    this.isLoading = false;

    await commons.getBreadcrumb(this.breadcrumb);

  }

  get isGroupBudgetMailHiddenUser() {
    return commons?.userInfo.groupBudgetMode
  }

  get userNotificationSettings() {
    const point_receipt: boolean =
      this.wantMail.want_point_given_mail == "1" ? true : false;
    const point_balance: boolean =
      this.wantMail.want_point_balance_mail == "1" ? true : false;
    const point_expiration_date: boolean =
      this.wantMail.want_point_expiration_mail == "1" ? true : false;
    const thanks_thread: boolean =
      this.wantMail.want_thanks_thread_mail == "1" ? true : false;
    const thanks_remind: boolean =
      this.wantMail.want_thanks_remind_mail == "1" ? true : false;
    const thanks_point: boolean =
      this.wantMail.approval_result_notification_mail == "1" ? true : false;
    const magazine: boolean =
      this.wantMail.want_mailmagazine == "1" ? true : false;

    const userDetails = auth.user;

    const gb_point_receipt: boolean =
      this.wantMail.gb_want_point_given_mail == "1" ? true : false;
    const gb_point_balance: boolean =
      this.wantMail.gb_want_point_balance_mail == "1" ? true : false;
    const gb_point_expiration_date: boolean =
      this.wantMail.gb_want_point_expiration_date == "1" ? true : false;

    return {
      user: userDetails.user_id,
      point_receipt: point_receipt,
      point_balance: point_balance,
      point_expiration_date: point_expiration_date,
      thanks_thread: thanks_thread,
      thanks_remind: thanks_remind,
      thanks_point_result: thanks_point,
      magazine: magazine,
      gb_point_receipt: gb_point_receipt,
      gb_point_balance: gb_point_balance,
      gb_point_expiration_date: gb_point_expiration_date
    };
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME1);
  }
  
  get sortedWantMailList() {
    return [
      this.wantMailList[0],
      this.wantMailList[1],
      this.wantMailList[2],
      this.wantMailList[5],
      this.wantMailList[6],
      this.wantMailList[3],
      this.wantMailList[4]
    ];
  }
  
  get isSp() {
    return globals.isLargeBreakpointWidthOfSp;
  }

  // 現在表示中の吹き出しの文言
  get activeTooltipText(): string {
    const target = this.wantMailList.find(
      item => item.key === this.activeTooltipKey
    );
    return target?.tooltip ?? "";
  }

  // PC: マウスオーバーで表示
  showTooltipPc(key: string) {
    if (!this.isSp) {
      this.activeTooltipKey = key;
    }
  }

  // PC: マウスアウトで非表示
  hideTooltipPc() {
    if (!this.isSp) {
      this.activeTooltipKey = "";
    }
  }

  // SP: タップでモーダル表示/非表示を切り替え
  toggleTooltip(key: string) {
    if (this.isSp) {
      this.activeTooltipKey = this.activeTooltipKey === key ? "" : key;
    }
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-m14 {
  &__pref {
    width: rem(256);
  }
}
.group-budget {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-weight: bold;
  font-size: 1.25rem;
}
// ラベル直後にインライン表示（吹き出しの基準位置）
.p-mail-help-wrapper {
  position: relative;
  display: inline-block;
  margin-left: 4px;
}
.p-mail-help {
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  color: #6264a7;
}
.p-mail-help__tooltip {
  position: absolute;
  width: 250px;
  padding: 8px 12px;
  font-weight: 400;
  background: #333;
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  z-index: 2000;

  @include pc {
    left: calc(100% + 8px);
    top: 0;
  }

  @include sp {
    left: 0;
    top: calc(100% + 5px);
  }
}
.p-help-modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .4);
  z-index: 3000;
}

.p-help-modal {
  width: 300px;
  max-width: calc(100vw - 32px);

  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .2);
}

.p-help-modal__body {
  padding: 12px;
  line-height: 1.8;
}

</style>
